/**
 * Minimal JWT reader for token freshness/identity checks. It decodes claims only and
 * does NOT verify the signature — verification is the server's job. The miniapp uses this
 * purely to decide when its embedded auth token is stale and whether a stored token
 * belongs to the same session, so no dependency (e.g. jwt-decode) is warranted.
 */

interface JwtClaims {
  // Standard JWT expiry: seconds since the Unix epoch.
  exp?: number;
  // Subject (user) and session identifiers — used to bind a stored token to a launch.
  sub?: string;
  sessionUuid?: string;
}

const base64UrlDecode = (segment: string): string => {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');

  return atob(padded);
};

/** Decode a JWT's payload claims without verifying it. Returns null when unreadable. */
const decodeClaims = (token?: string): JwtClaims | null => {
  const payload = token?.replace(/^bearer\s/i, '').split('.')[1];

  if (!payload) {
    return null;
  }

  try {
    return JSON.parse(base64UrlDecode(payload)) as JwtClaims;
  } catch {
    return null;
  }
};

/** Reads a JWT's `exp` (seconds). Returns null for a missing/malformed token. */
export const getTokenExpiry = (token?: string): number | null => {
  const exp = decodeClaims(token)?.exp;

  return typeof exp === 'number' ? exp : null;
};

/**
 * True when a token is at/near expiry (or unreadable). `skewSeconds` refreshes
 * slightly ahead of the hard expiry to avoid racing a request against it. An
 * unreadable expiry is treated as expired so we err toward refreshing.
 */
export const isTokenExpired = (token?: string, skewSeconds = 30): boolean => {
  const exp = getTokenExpiry(token);

  if (exp === null) {
    return true;
  }

  return exp * 1000 <= Date.now() + skewSeconds * 1000;
};

/**
 * True when `candidate` expires strictly later than `current` — i.e. it is the
 * fresher token. Used to stop a stale launch query-param from clobbering a token
 * we already refreshed. A candidate with an unreadable expiry is never fresher.
 */
export const isFresherToken = (candidate?: string, current?: string): boolean => {
  const candidateExp = getTokenExpiry(candidate);
  const currentExp = getTokenExpiry(current);

  if (candidateExp === null) {
    return false;
  }

  if (currentExp === null) {
    return true;
  }

  return candidateExp > currentExp;
};

/**
 * True when both tokens belong to the same authenticated session — same subject AND
 * same session id. Used so a stored (refreshed) token is only ever preferred over the
 * launch token when it's the SAME session, never a prior session's leftover on this
 * origin. A token whose identity claims are unreadable/absent is never a match.
 */
export const isSameSession = (a?: string, b?: string): boolean => {
  const ca = decodeClaims(a);
  const cb = decodeClaims(b);

  return Boolean(ca?.sub && ca.sub === cb?.sub && ca?.sessionUuid && ca.sessionUuid === cb?.sessionUuid);
};
